import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Calendar, Image, MessageCircle, ArrowRight, Star, Quote, Instagram } from 'lucide-react';
import { studioInfo, testimonials } from '../data/mock';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import axios from 'axios';

// Constants
const CAROUSEL_INTERVAL_MS = 4000;
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const [emblaRef] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: CAROUSEL_INTERVAL_MS })]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [latestPhotos, setLatestPhotos] = useState([]);
  
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events`);
        if (response.data.success) {
          setUpcomingEvents(response.data.data || []);
        }
      } catch (error) {
        setUpcomingEvents([]);
      }
    };
    const fetchLatestPhotos = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/portfolio`);
        if (response.data.success) {
          setLatestPhotos((response.data.data || []).filter(item => item.type !== 'video').slice(0, 8));
        }
      } catch (error) {
        setLatestPhotos([]);
      }
    };
    fetchEvents();
    fetchLatestPhotos();
  }, []);
  
  // Memoize approved testimonials to avoid filtering on every render
  const approvedTestimonials = useMemo(() => 
    testimonials.filter(t => t.approved), 
    []
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-white">
        <div className="container mx-auto">
          {/* Logo Banner - Centered at Top */}
          <div className="flex justify-center mb-8">
            <img 
              src="/logo.png" 
              alt="Creative Minds Logo" 
              className="h-40 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          {/* Main Content - Centered */}
          <div className="text-center max-w-4xl mx-auto space-y-6 mb-16">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
              <span className="text-base font-bold text-white">Unleash Your Creativity</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-black shimmer-heading leading-tight pb-2">
              {studioInfo.name}
            </h1>
            
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              {studioInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Scrolling Banner */}
      <div className="relative overflow-hidden py-6" style={{ background: 'linear-gradient(90deg, #d4a017, #ec4899, #9333ea, #3b82f6)' }}>
        <div className="sparkle-overlay"></div>
        <div className="flex animate-scroll whitespace-nowrap" data-testid="scrolling-banner">
          {/* First set */}
          <div className="flex min-w-max">
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
          </div>
          {/* Duplicate set for seamless loop */}
          <div className="flex min-w-max">
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
            <span className="text-4xl font-black text-white mx-8">Creativity + Chaos = Magic ✨</span>
          </div>
        </div>
      </div>

      {/* Instagram Feed */}
      {latestPhotos.length > 0 && (
        <section className="py-20 px-6 bg-white" data-testid="instagram-section">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-12">
              <div className="text-center md:text-left">
                <h2 className="text-4xl font-bold shimmer-heading">Fresh From the Studio</h2>
                <p className="text-gray-600 mt-2">Our latest creations - follow along on Instagram!</p>
              </div>
              <a 
                href="https://www.instagram.com/creativemindsfargo/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-follow-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white hover:scale-105 hover:shadow-2xl transition-all duration-200"
                style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
              >
                <Instagram className="w-5 h-5" />
                Follow @creativemindsfargo
              </a>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {latestPhotos.map((photo) => (
                <a
                  key={photo.id}
                  href="https://www.instagram.com/creativemindsfargo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg"
                  data-testid="instagram-photo-tile"
                >
                  <img
                    src={photo.url.startsWith('/') ? `${API_URL}${photo.url}` : photo.url}
                    alt={photo.title || 'Creative Minds studio creation'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-pink-600/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                    <Instagram className="w-8 h-8 text-white" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto">
          {/* Upcoming Events - Moved Here */}
          <div>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold shimmer-heading">Upcoming Events</h2>
              <Link 
                to="/events" 
                className="text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-2"
              >
                View All
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.length > 0 ? upcomingEvents.slice(0, 3).map((event) => (
                <Link
                  key={event.id}
                  to="/events"
                  className={`bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer block border-2 border-amber-200 ${
                    event.isPopup ? 'border-pink-400 relative overflow-hidden' : ''
                  }`}
                >
                  {event.isPopup && (
                    <>
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-pink-500 to-amber-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                        SPECIAL EVENT!
                      </div>
                      <div className="absolute -top-10 -right-10 w-32 h-32 bg-pink-200 rounded-full opacity-20"></div>
                    </>
                  )}
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`px-3 py-1 ${
                      event.isPopup 
                        ? 'bg-gradient-to-r from-pink-500 to-amber-500 text-white' 
                        : 'bg-amber-100 text-amber-600'
                    } rounded-lg text-sm font-semibold`}>
                      {event.spotsAvailable} spots left
                    </div>
                  </div>
                  <h3 className={`${
                    event.isPopup ? 'text-xl' : 'text-lg'
                  } font-bold mb-2 text-gray-900`}>
                    {event.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">{event.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{new Date(event.date).toLocaleDateString()}</span>
                    <span>{event.time}</span>
                  </div>
                  <div className="mt-4 text-center">
                    <span className="text-amber-500 font-semibold hover:text-amber-600 flex items-center justify-center gap-1">
                      Click to Book <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              )) : (
                <div className="md:col-span-3 bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 border-2 border-yellow-300 rounded-2xl p-8 text-center" data-testid="no-upcoming-events">
                  <p className="text-lg font-bold text-gray-900 mb-2">No special events scheduled right now</p>
                  <p className="text-gray-600 mb-4">But the studio is always open by appointment - book your own creative session anytime!</p>
                  <Link to="/events" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-pink-600 text-white rounded-xl font-bold hover:scale-105 transition-transform duration-200">
                    Book a Session <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer - directly under Upcoming Events */}
      <section className="py-16 px-6 bg-gradient-to-b from-amber-50 via-pink-50 to-purple-50">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 shimmer-heading">What We Offer</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group p-8 bg-white border-2 border-amber-200 rounded-2xl hover:shadow-2xl hover:shadow-amber-200 transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Flexible Sessions</h3>
              <p className="text-gray-700 leading-relaxed">
                Book individual sessions, group events, or corporate team building. We accommodate all creativity levels!
              </p>
            </div>

            <div className="group p-8 bg-white border-2 border-blue-200 rounded-2xl hover:shadow-2xl hover:shadow-blue-200 transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-sky-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Image className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">All Materials Included</h3>
              <p className="text-gray-700 leading-relaxed">
                Canvas, paint brushes, sponges, hazmat suits, eye and shoe protection - everything you need to create your masterpiece is provided.
              </p>
            </div>

            <div className="group p-8 bg-white border-2 border-purple-200 rounded-2xl hover:shadow-2xl hover:shadow-purple-200 transition-all duration-300 hover:-translate-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Expert Guidance</h3>
              <p className="text-gray-700 leading-relaxed">
                Our friendly team is here to help, whether you want tips or prefer to express freely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center">
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/events" 
                className="px-10 py-5 bg-gradient-to-r from-amber-500 to-pink-600 hover:from-amber-400 hover:to-pink-500 text-white rounded-xl font-black text-lg hover:shadow-2xl hover:shadow-pink-500/50 hover:scale-105 transition-all duration-200 flex items-center gap-2 border-2 border-amber-400"
              >
                Book a Session
                <ArrowRight className="w-6 h-6" />
              </Link>
              <Link 
                to="/portfolio" 
                className="px-10 py-5 bg-white hover:bg-yellow-50 text-purple-700 rounded-xl font-black text-lg hover:shadow-2xl hover:shadow-purple-300 hover:scale-105 transition-all duration-200 border-3 border-purple-400"
              >
                View Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20 px-6 bg-gradient-to-b from-purple-50 via-pink-50 to-amber-50">
        <div className="container mx-auto">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold shimmer-heading">Guest Experiences</h2>
              <p className="text-gray-700 mt-2">See what our creative community is saying</p>
            </div>
            <Link 
              to="/testimonials" 
              className="text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-2"
            >
              View All
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {approvedTestimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                >
                  <div className="bg-white border-2 border-purple-200 p-8 rounded-2xl shadow-lg hover:shadow-purple-300 h-full">
                    <Quote className="w-10 h-10 text-amber-500 mb-4" />
                    
                    <p className="text-gray-700 leading-relaxed mb-6 line-clamp-4">
                      "{testimonial.comment}"
                    </p>
                    
                    <div className="flex items-center gap-4 mt-auto">
                      <img 
                        src={testimonial.photo} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400"
                      />
                      <div>
                        <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                        <div className="flex gap-1 mt-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={`star-${testimonial.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <p className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-pink-600 font-semibold">
                        {testimonial.sessionAttended}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700 text-white relative overflow-hidden">
        <div className="sparkle-overlay"></div>
        <div className="container mx-auto text-center relative">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Create?</h2>
          <p className="text-xl text-purple-100 mb-10 max-w-2xl mx-auto">
            Join us for an unforgettable creative experience. Express yourself freely in our vibrant studio!
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-yellow-300 via-pink-200 to-blue-300 text-gray-900 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            Get in Touch
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};
